package com.schoolconnect.app.feature.academicyears

import androidx.compose.ui.graphics.Color
import com.schoolconnect.app.datasource.network.response.AcademicYearAnalyticsResponse
import com.schoolconnect.app.feature.common.ServerPagedListUiState
import com.schoolconnect.app.feature.common.ServerPagingState

enum class AcademicYearStatus(
    val label: String,
    val accent: Color,
    val accentSoft: Color,
    val dotColor: Color
) {
    ACTIVE(
        label = "Current",
        accent = Color(0xFF16A34A),
        accentSoft = Color(0xFFEAF8F0),
        dotColor = Color(0xFF16A34A)
    ),
    UPCOMING(
        label = "Upcoming",
        accent = Color(0xFFF59E0B),
        accentSoft = Color(0xFFFFF0E3),
        dotColor = Color(0xFFF59E0B)
    ),
    CLOSED(
        label = "Completed",
        accent = Color(0xFF6B7280),
        accentSoft = Color(0xFFF3F4F6),
        dotColor = Color(0xFF6B7280)
    );

    val apiValue: String
        get() = when (this) {
            ACTIVE -> "Current"
            UPCOMING -> "Upcoming"
            CLOSED -> "Completed"
        }
}

enum class AcademicYearsFilter {
    ALL,
    ACTIVE,
    UPCOMING,
    CLOSED
}

internal fun AcademicYearsFilter.lifecycleLabel(): String = when (this) {
    AcademicYearsFilter.ALL -> "All"
    AcademicYearsFilter.ACTIVE -> "Current"
    AcademicYearsFilter.UPCOMING -> "Upcoming"
    AcademicYearsFilter.CLOSED -> "Completed"
}

internal fun String.toAcademicYearsFilter(): AcademicYearsFilter = when (this) {
    "Current" -> AcademicYearsFilter.ACTIVE
    "Upcoming" -> AcademicYearsFilter.UPCOMING
    "Completed" -> AcademicYearsFilter.CLOSED
    else -> AcademicYearsFilter.ALL
}

enum class AcademicYearActiveStatusFilter {
    ALL,
    ACTIVE,
    INACTIVE
}

internal fun AcademicYearActiveStatusFilter.displayLabel(): String = when (this) {
    AcademicYearActiveStatusFilter.ALL -> "All"
    AcademicYearActiveStatusFilter.ACTIVE -> "Active"
    AcademicYearActiveStatusFilter.INACTIVE -> "Inactive"
}

internal fun String.toAcademicYearActiveStatusFilter(): AcademicYearActiveStatusFilter = when (this) {
    "Active" -> AcademicYearActiveStatusFilter.ACTIVE
    "Inactive" -> AcademicYearActiveStatusFilter.INACTIVE
    else -> AcademicYearActiveStatusFilter.ALL
}

enum class AcademicYearsDialogMode {
    NONE,
    DETAILS,
    EDITOR,
    TOGGLE_CONFIRM,
    DELETE_CONFIRM
}

enum class AcademicYearsSortField(val apiField: String) {
    ACADEMIC_YEAR("label"),
    SCHOOL("school_name"),
    START_DATE("start_date"),
    END_DATE("end_date"),
    LIFECYCLE("lifecycle"),
    STATUS("is_active")
}

data class AcademicYearListItem(
    val id: String,
    val schoolId: String = "",
    val academicYear: String,
    val school: String,
    val startDate: String,
    val endDate: String,
    val status: AcademicYearStatus,
    val isActive: Boolean = true,
    val createdOn: String = "10 May 2026",
    val updatedOn: String = "14 Jun 2026",
    val createdBy: String = "Super Admin",
    val lastModifiedBy: String = "School Administrator"
)

data class AcademicYearEditorDraft(
    val id: String = "",
    val schoolId: String = "",
    val academicYear: String = "",
    val school: String = "",
    val startDate: String = "",
    val endDate: String = "",
    val status: AcademicYearStatus = AcademicYearStatus.UPCOMING,
    val isActive: Boolean = true,
    val createdOn: String = "",
    val updatedOn: String = ""
)

data class AcademicYearFilterCriteria(
    val searchQuery: String = "",
    val school: String = "",
    val status: AcademicYearsFilter = AcademicYearsFilter.ALL,
    val startYear: String = "",
    val endYear: String = "",
    val createdFrom: String = "",
    val createdTo: String = "",
    val activeStatus: AcademicYearActiveStatusFilter = AcademicYearActiveStatusFilter.ALL
)

data class AcademicYearsUiState(
    override val paging: ServerPagingState = ServerPagingState(),
    val isLoading: Boolean = false,
    val isAnalyticsLoading: Boolean = false,
    val isEditorSaving: Boolean = false,
    val errorMessage: String = "",
    val infoMessage: String = "",
    val filters: AcademicYearFilterCriteria = AcademicYearFilterCriteria(),
    val sortField: AcademicYearsSortField = AcademicYearsSortField.LIFECYCLE,
    val sortAscending: Boolean = true,
    val openMenuId: String? = null,
    val dialogMode: AcademicYearsDialogMode = AcademicYearsDialogMode.NONE,
    val dialogItemId: String? = null,
    val editorDraft: AcademicYearEditorDraft = AcademicYearEditorDraft(),
    val items: List<AcademicYearListItem> = emptyList(),
    val schoolOptions: List<String> = emptyList(),
    val academicYearAnalytics: AcademicYearAnalyticsResponse? = null
) : ServerPagedListUiState

internal fun academicYearFilterLabels(filters: AcademicYearFilterCriteria): List<String> = buildList {
    if (filters.searchQuery.isNotBlank()) add("Search: ${filters.searchQuery.trim()}")
    if (filters.school.isNotBlank()) add("School: ${filters.school.trim()}")
    if (filters.startYear.isNotBlank()) add("Start year: ${filters.startYear.trim()}")
    if (filters.endYear.isNotBlank()) add("End year: ${filters.endYear.trim()}")
    if (filters.createdFrom.isNotBlank() || filters.createdTo.isNotBlank()) {
        val from = filters.createdFrom.ifBlank { "Any" }
        val to = filters.createdTo.ifBlank { "Any" }
        add("Created: $from to $to")
    }
}

internal fun String.toAcademicYearStatus(): AcademicYearStatus = when (trim().lowercase()) {
    "current" -> AcademicYearStatus.ACTIVE
    "completed" -> AcademicYearStatus.CLOSED
    else -> AcademicYearStatus.UPCOMING
}

sealed interface AcademicYearsUiEvent {
    data object BackClicked : AcademicYearsUiEvent
    data object RefreshClicked : AcademicYearsUiEvent
    data class SearchChanged(val query: String) : AcademicYearsUiEvent
    data class FilterApplied(val filters: AcademicYearFilterCriteria) : AcademicYearsUiEvent
    data class SortChanged(val field: AcademicYearsSortField) : AcademicYearsUiEvent
    data class PageChanged(val pageIndex: Int) : AcademicYearsUiEvent
    data class RowsPerPageChanged(val rowsPerPage: Int) : AcademicYearsUiEvent
    data class MenuToggled(val itemId: String?) : AcademicYearsUiEvent
    data object MenuClosed : AcademicYearsUiEvent
    data object AddClicked : AcademicYearsUiEvent
    data class ViewDetailsClicked(val itemId: String) : AcademicYearsUiEvent
    data class EditClicked(val itemId: String) : AcademicYearsUiEvent
    data class ToggleStatusClicked(val itemId: String) : AcademicYearsUiEvent
    data class DeleteClicked(val itemId: String) : AcademicYearsUiEvent
    data object DialogDismissed : AcademicYearsUiEvent
    data object DialogSaveRequested : AcademicYearsUiEvent
    data object ToggleStatusConfirmed : AcademicYearsUiEvent
    data object DeleteConfirmed : AcademicYearsUiEvent
    data class EditorAcademicYearChanged(val value: String) : AcademicYearsUiEvent
    data class EditorSchoolChanged(val value: String) : AcademicYearsUiEvent
    data class EditorStartDateChanged(val value: String) : AcademicYearsUiEvent
    data class EditorEndDateChanged(val value: String) : AcademicYearsUiEvent
    data class EditorStatusChanged(val value: AcademicYearStatus) : AcademicYearsUiEvent
}
